import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useRef, useCallback } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { logout } from "../api/auth.js";
import { useSession } from "../hooks/use-session.js";

interface AppShellProps {
  children: React.ReactNode;
}

export function AppShell({ children }: AppShellProps) {
  const { user } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const hamburgerRef = useRef<HTMLButtonElement>(null);

  const logoutMutation = useMutation({
    mutationFn: logout,
    onSuccess: () => {
      qc.clear();
      void navigate("/sign-in");
    },
    onError: () => {
      // Even if the server-side logout fails, clear local state
      qc.clear();
      void navigate("/sign-in");
    },
  });

  // Close menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Lock body scroll when menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen]);

  // Focus trap and Escape key
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!mobileMenuOpen) return;

      if (e.key === "Escape") {
        setMobileMenuOpen(false);
        hamburgerRef.current?.focus();
        return;
      }

      if (e.key === "Tab" && menuRef.current) {
        const focusable = menuRef.current.querySelectorAll<HTMLElement>(
          'a, button, [tabindex]:not([tabindex="-1"])',
        );
        if (focusable.length === 0) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    },
    [mobileMenuOpen],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  // Focus the close button when menu opens
  useEffect(() => {
    if (mobileMenuOpen && menuRef.current) {
      const closeBtn = menuRef.current.querySelector<HTMLElement>(
        ".mobile-nav__close",
      );
      closeBtn?.focus();
    }
  }, [mobileMenuOpen]);

  return (
    <div className="app-shell">
      <a href="#main-content" className="skip-to-content">
        Skip to main content
      </a>
      <header className="app-shell__header">
        <Link to="/app" className="app-shell__logo">
          Accessi<span>Monitor</span>
        </Link>
        <nav className="app-shell__nav app-shell__nav--desktop">
          {user && <span>{user.email}</span>}
          <Link to="/help" className="btn btn--secondary btn--sm">
            Help
          </Link>
          <button
            className="btn btn--secondary btn--sm"
            onClick={() => logoutMutation.mutate()}
            disabled={logoutMutation.isPending}
          >
            Sign out
          </button>
        </nav>
        <button
          ref={hamburgerRef}
          type="button"
          className="hamburger-btn"
          onClick={() => setMobileMenuOpen(true)}
          aria-expanded={mobileMenuOpen}
          aria-controls="app-mobile-nav"
          aria-label="Open navigation menu"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>
      </header>

      {mobileMenuOpen && (
        <div
          className="mobile-nav-backdrop"
          onClick={() => setMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      <div
        ref={menuRef}
        id="app-mobile-nav"
        className={`mobile-nav ${mobileMenuOpen ? "mobile-nav--open" : ""}`}
        role="dialog"
        aria-modal={mobileMenuOpen || undefined}
        aria-label="Navigation menu"
        aria-hidden={!mobileMenuOpen}
      >
        <div className="mobile-nav__header">
          <span className="mobile-nav__title">Menu</span>
          <button
            type="button"
            className="mobile-nav__close"
            onClick={() => {
              setMobileMenuOpen(false);
              hamburgerRef.current?.focus();
            }}
            aria-label="Close navigation menu"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <nav className="mobile-nav__links">
          {user && (
            <span className="mobile-nav__email">{user.email}</span>
          )}
          <Link to="/help">Help</Link>
          <button
            type="button"
            className="mobile-nav__link-btn"
            onClick={() => {
              setMobileMenuOpen(false);
              logoutMutation.mutate();
            }}
            disabled={logoutMutation.isPending}
          >
            Sign out
          </button>
        </nav>
      </div>

      <main id="main-content" className="app-shell__main">{children}</main>
    </div>
  );
}
